'use client';

import { ProductLandingComponent, ProductLandingPageType } from '@/types/product';
import ProductsHeroEnquiry from '@/components/sections/Products/ProductsHeroEnquiry';
import ProductTypesSection from '@/components/sections/Products/ProductTypesSection';
import ProductsCostPlanBanner from '@/components/sections/Products/ProductsCostPlanBanner';
import WitnessFarqEngineering from '@/components/sections/Products/WitnessFarqEngineering';
import ProductVoicesOfTrustShowcase from '@/components/sections/Products/ProductVoicesOfTrustShowcase';
import ProductsIntroStatsSection from '@/components/sections/Products/ProductsIntroStatsSection';
import CommonSpacer from '@/components/sections/shared/CommonSpacer';
import WebVitals from '@/components/performance/WebVitals';
import { getImageUrl } from '@/lib/homepage-mappers';
import { getStrapiOriginalMediaUrl } from '@/lib/strapi-media-url';
import { type ReactNode } from 'react';

interface ProductListProps {
  locale: string;
  productLandingData: ProductLandingPageType | null;
  /**
   * Streaming slot for the FAQ + enquiry block — rendered by an async
   * Server Component wrapped in `<Suspense>` in `page.tsx`.
   */
  faqSlot?: ReactNode;
}

function isSpacerComponent(component: ProductLandingComponent) {
  return (
    component.__component === 'common.spacer-top' ||
    component.__component === 'common.spacer-full' ||
    component.__component === 'common.spacer-bottom'
  );
}

function shouldRenderProductComponent(
  components: ProductLandingComponent[],
  index: number,
): boolean {
  const component = components[index];
  const previous = components[index - 1];

  // CMS may place a spacer after the Farq video block — skip it so the FAQ
  // section follows directly (video section already has its own bottom rhythm).
  if (
    isSpacerComponent(component) &&
    previous?.__component === 'modules.video-sectionwith-heading'
  ) {
    return false;
  }

  return true;
}

function renderProductComponent(
  component: ProductLandingComponent,
  locale: string,
  productLandingData: ProductLandingPageType,
  faqSlot?: ReactNode,
) {
  switch (component.__component) {
    case 'common.spacer-top':
    case 'common.spacer-full':
    case 'common.spacer-bottom':
      return <CommonSpacer key={component.id} zone={component} />;

    case 'modules.image-grid':
      return (
        <ProductsIntroStatsSection
          key={component.id}
          locale={locale}
          description={productLandingData.bannerDescription}
          imageGridComponent={component}
        />
      );

    case 'products.product-relation':
      return (
        <div key={component.id} id="product-range">
          <ProductTypesSection locale={locale} relation={component} />
        </div>
      );

    case 'modules.text-image':
      return (
        <ProductsCostPlanBanner key={component.id} locale={locale} textImageComponent={component} />
      );

    case 'common.video-testimonial':
    case 'common.testimonial':
      return <ProductVoicesOfTrustShowcase key={component.id} testimonialComponent={component} />;

    case 'modules.video-sectionwith-heading':
      return (
        <WitnessFarqEngineering
          key={component.id}
          videoComponent={component}
          sectionClassName="pt-0"
        />
      );

    case 'modules.faqwith-form':
      return faqSlot ? <div key={component.id}>{faqSlot}</div> : null;

    default:
      return null;
  }
}

export default function ProductList({ locale, productLandingData, faqSlot }: ProductListProps) {
  const heroMediaMime = productLandingData?.bannerHeroImage?.mime || undefined;
  const isHeroVideo = Boolean(heroMediaMime?.startsWith('video/'));
  const heroMediaUrl = isHeroVideo
    ? getImageUrl(productLandingData?.bannerHeroImage as never) || undefined
    : getStrapiOriginalMediaUrl(productLandingData?.bannerHeroImage as never) || undefined;
  const heroPosterUrl = isHeroVideo
    ? undefined
    : getImageUrl(productLandingData?.bannerHeroImage as never) || undefined;

  const components = productLandingData?.components ?? [];

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-black" data-section-blur="false">
      <WebVitals />
      <ProductsHeroEnquiry
        eyebrow={productLandingData?.bannerTitle}
        headline={productLandingData?.bannerSubtitle}
        headlineRichText={productLandingData?.titleRichText}
        heroMediaUrl={heroMediaUrl}
        heroMediaMime={heroMediaMime}
        heroPosterUrl={heroPosterUrl}
      />

      {productLandingData
        ? components
            .filter((_, index) => shouldRenderProductComponent(components, index))
            .map((component) =>
              renderProductComponent(component, locale, productLandingData, faqSlot),
            )
        : null}
    </div>
  );
}
